/**
 * Owner: Masa Bayu
 * File: author-security.ts
 * Date:Time: 2026-09-16 13:43
 * Description: T6 author-platform security — E1–E7 account · publishing · journey.
 */
export const QXREAD_T6_E1_PROHIBITED = [
    'QXRead → host QXW mutation in Author Platform',
    'QXRead → mutate QXP from Author Dashboard',
    'QXRead → invent earnings without Commerce Orders',
    'QXRead → mint Entitlement from Author surfaces',
    'QXRead → approve publishing without Admin when policy requires',
    'QXRead → access another AuthorAccount without own-resource scope',
    'QXRead → treat Chrome author mode as security authority',
];
export const QXREAD_T6_E1_SECURITY = Object.freeze({
    authorDoesNotHostQxwMutation: true,
    authorDoesNotMutateQxp: true,
    earningsDerivedFromOrdersOnly: true,
    authorDoesNotMintEntitlement: true,
    adminApprovalStillRequiredWhenPolicyRequires: true,
    ownResourceScopeRequired: true,
    chromeModeIsNotSecurityAuthority: true,
    qxwriterRemainsAuthoringAuthority: true,
    stripeRemainsPspUnderCommerce: true,
});
export function isT6E1ProhibitedOperation(operation) {
    return QXREAD_T6_E1_PROHIBITED.includes(operation);
}
export function assertT6E1SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E1_SECURITY)) {
        const expected = QXREAD_T6_E1_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E1 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E1_SECURITY;
}
export const QXREAD_T6_E2_PROHIBITED = [
    'QXRead → host QXW mutation via PublishRequest',
    'QXRead → mutate QXP via PublishRequest',
    'QXRead → treat PublishRequest as QXW document',
    'QXRead → author self-approve when Admin policy requires',
    'QXRead → mint Entitlement via PublishRequest',
    'QXRead → submit PublishRequest without readiness',
    'QXRead → bypass own-resource on PublishRequest',
];
export const QXREAD_T6_E2_SECURITY = Object.freeze({
    publishRequestDoesNotHostQxwMutation: true,
    publishRequestDoesNotMutateQxp: true,
    publishRequestIsNotQxwDocument: true,
    authorCannotSelfApproveWhenPolicyRequires: true,
    publishRequestDoesNotMintEntitlement: true,
    submitRequiresReadiness: true,
    publishRequestOwnResourceRequired: true,
    adminReviewRemainsT7: true,
});
export function isT6E2ProhibitedOperation(operation) {
    return QXREAD_T6_E2_PROHIBITED.includes(operation);
}
export function assertT6E2SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E2_SECURITY)) {
        const expected = QXREAD_T6_E2_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E2 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E2_SECURITY;
}
export const QXREAD_T6_E3_PROHIBITED = [
    'QXRead → mutate QXP via StorefrontConfig',
    'QXRead → host QXW mutation via Storefront',
    'QXRead → mint Entitlement via Storefront preview',
    'QXRead → execute Reader from Storefront preview',
    'QXRead → trust client storefront price as checkout authority',
    'QXRead → bypass own-resource on StorefrontConfig',
    'QXRead → list hidden publication as public shopper preview',
];
export const QXREAD_T6_E3_SECURITY = Object.freeze({
    storefrontDoesNotMutateQxp: true,
    storefrontDoesNotHostQxwMutation: true,
    storefrontPreviewDoesNotMintEntitlement: true,
    storefrontPreviewDoesNotExecuteReader: true,
    clientPriceIsHintOnly: true,
    storefrontOwnResourceRequired: true,
    hiddenListingBlocksShopperPreview: true,
    commerceRecomputesAtCheckout: true,
});
export function isT6E3ProhibitedOperation(operation) {
    return QXREAD_T6_E3_PROHIBITED.includes(operation);
}
export function assertT6E3SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E3_SECURITY)) {
        const expected = QXREAD_T6_E3_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E3 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E3_SECURITY;
}
export const QXREAD_T6_E4_PROHIBITED = [
    'QXRead → invent SaleRecord without confirmed Order',
    'QXRead → project sales from pending Order',
    'QXRead → project sales from failed payment',
    'QXRead → UI-mint author sales totals',
    'QXRead → bypass own-resource on SaleRecord',
    'QXRead → treat Author sales view as payment authority',
    'QXRead → mutate Commerce Order from Author sales',
];
export const QXREAD_T6_E4_SECURITY = Object.freeze({
    salesDerivedFromConfirmedOrdersOnly: true,
    pendingOrdersDoNotCreateSales: true,
    failedPaymentDoesNotCreateSales: true,
    authorUiDoesNotMintSales: true,
    salesOwnResourceRequired: true,
    authorSalesIsNotPaymentAuthority: true,
    authorSalesDoesNotMutateCommerceOrder: true,
    stripeRemainsPspUnderCommerce: true,
});
export function isT6E4ProhibitedOperation(operation) {
    return QXREAD_T6_E4_PROHIBITED.includes(operation);
}
export function assertT6E4SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E4_SECURITY)) {
        const expected = QXREAD_T6_E4_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E4 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E4_SECURITY;
}
export const QXREAD_T6_E5_PROHIBITED = [
    'QXRead → invent EarningLedger without SaleRecord',
    'QXRead → UI-mint author earnings balances',
    'QXRead → execute Stripe payout from Author earnings UI',
    'QXRead → bypass step-up on PayoutRequest',
    'QXRead → bypass own-resource on EarningLedger',
    'QXRead → treat Author earnings view as payment authority',
    'QXRead → mutate Commerce settlement from Author earnings',
];
export const QXREAD_T6_E5_SECURITY = Object.freeze({
    earningsDerivedFromSaleRecordsOnly: true,
    authorUiDoesNotMintEarnings: true,
    authorUiDoesNotExecutePayout: true,
    payoutRequestRequiresStepUp: true,
    earningsOwnResourceRequired: true,
    authorEarningsIsNotPaymentAuthority: true,
    authorEarningsDoesNotMutateCommerceSettlement: true,
    stripePayoutRemainsUnderCommerce: true,
});
export function isT6E5ProhibitedOperation(operation) {
    return QXREAD_T6_E5_PROHIBITED.includes(operation);
}
export function assertT6E5SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E5_SECURITY)) {
        const expected = QXREAD_T6_E5_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E5 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E5_SECURITY;
}
export const QXREAD_T6_E6_PROHIBITED = [
    'QXRead → expose raw Reader telemetry in Author analytics',
    'QXRead → invent analytics without SaleRecord or supplied aggregates',
    'QXRead → UI-mint author analytics metrics',
    'QXRead → execute Reader from Author analytics',
    'QXRead → bypass own-resource on AuthorAnalyticsSlice',
    'QXRead → dump reading position or entitlement state into analytics',
    'QXRead → treat Author analytics as Commerce payment authority',
];
export const QXREAD_T6_E6_SECURITY = Object.freeze({
    analyticsAggregatesFromSaleRecordsOnly: true,
    analyticsDoesNotIncludeRawReaderTelemetry: true,
    authorUiDoesNotMintAnalytics: true,
    analyticsDoesNotExecuteReader: true,
    analyticsOwnResourceRequired: true,
    analyticsDoesNotDumpReadingPosition: true,
    authorAnalyticsIsNotPaymentAuthority: true,
    audienceMetricsMustBeSuppliedAggregate: true,
});
export function isT6E6ProhibitedOperation(operation) {
    return QXREAD_T6_E6_PROHIBITED.includes(operation);
}
export function assertT6E6SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E6_SECURITY)) {
        const expected = QXREAD_T6_E6_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E6 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E6_SECURITY;
}
export const QXREAD_T6_E7_PROHIBITED = [
    'QXRead → host QXW mutation via author integration journey',
    'QXRead → mint Entitlement via author integration journey',
    'QXRead → execute payment from author integration journey',
    'QXRead → treat chrome author mode as security authority in journey',
    'QXRead → complete journey without publish readiness when policy requires',
    'QXRead → bypass own-resource on author integration journey',
    'QXRead → open Admin review from author journey without T7',
];
export const QXREAD_T6_E7_SECURITY = Object.freeze({
    journeyDoesNotHostQxwMutation: true,
    journeyDoesNotMintEntitlement: true,
    journeyDoesNotExecutePayment: true,
    chromeModeIsNotSecurityAuthority: true,
    journeyComposesE1ThroughE6: true,
    journeyOwnResourceRequired: true,
    adminReviewRemainsT7: true,
    qxwriterRemainsAuthoringAuthority: true,
});
export function isT6E7ProhibitedOperation(operation) {
    return QXREAD_T6_E7_PROHIBITED.includes(operation);
}
export function assertT6E7SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T6_E7_SECURITY)) {
        const expected = QXREAD_T6_E7_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T6-E7 security violated: ${key}`);
        }
    }
    return QXREAD_T6_E7_SECURITY;
}
//# sourceMappingURL=author-security.js.map